import React, { useEffect, useState } from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useBlessingClick from '../../hooks/useBlessingClick';

/**
 * StickyCTA — premium floating bar that slides up from the bottom of the
 * viewport once the user scrolls past the hero. Always-available CTA that
 * dramatically improves mobile conversion on long pages.
 *
 * Auto-hides when the user reaches the final CTA section / footer (so it
 * doesn't double up with the in-page CTA).
 */
const StickyCTA = ({ onOpenForm }) => {
  const { language } = useLanguage();
  const isHi = language === 'hi';
  const blessing = useBlessingClick();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handler = () => {
      const y = window.scrollY;
      const h = window.innerHeight;
      const docH = document.documentElement.scrollHeight;
      // Show after one viewport of scroll; hide near the very bottom (last 600px)
      const shouldShow = y > h * 0.6 && y < docH - h - 600;
      setVisible(shouldShow);
    };
    handler();
    window.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler);
      window.removeEventListener('resize', handler);
    };
  }, []);

  return (
    <div
      data-testid="sticky-cta"
      aria-hidden={!visible}
      className={`fixed inset-x-0 bottom-0 z-50 px-3 pb-3 sm:pb-4 pointer-events-none transition-all duration-500 ease-out ${
        visible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
    >
      <div className="relative max-w-3xl mx-auto pointer-events-auto">
        <div className="relative bg-gradient-to-r from-brand-maroon via-brand-maroon-dark to-brand-maroon rounded-2xl shadow-premium border border-brand-gold/40 overflow-hidden">
          {/* Decorative shimmer line */}
          <div
            className="absolute inset-x-0 top-0 h-px"
            style={{
              background:
                'linear-gradient(90deg, transparent 0%, #F3E5AB 50%, transparent 100%)',
            }}
          />
          <div
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              background:
                'radial-gradient(circle at 20% 50%, rgba(243,229,171,0.20) 0%, transparent 40%)',
            }}
          />

          <div className="relative flex items-center gap-3 sm:gap-5 px-4 sm:px-6 py-3">
            {/* Price block */}
            <div className="flex-shrink-0 hidden sm:flex items-baseline gap-1.5">
              <span className="text-white/55 line-through text-sm">₹999</span>
              <span
                className="font-outfit font-extrabold text-2xl sm:text-3xl text-brand-gold-light"
              >
                ₹99
              </span>
              <span className="bg-brand-gold text-brand-maroon-dark text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ml-1">
                90% OFF
              </span>
            </div>

            {/* Mobile-only compact info */}
            <div className="sm:hidden flex flex-col leading-tight">
              <span className="text-brand-gold-light text-[10px] uppercase tracking-[0.2em] font-bold">
                {isHi ? 'सीमित ऑफर' : 'Limited Offer'}
              </span>
              <span
                className="font-outfit font-extrabold text-xl text-brand-gold-light"
              >
                ₹99 <span className="text-white/60 text-xs line-through font-outfit font-normal">₹999</span>
              </span>
            </div>

            <button
              type="button"
              data-testid="sticky-cta-btn"
              onClick={(e) => {
                blessing(e);
                onOpenForm();
              }}
              className="group relative ml-auto flex-shrink-0 inline-flex items-center gap-2 bg-brand-gold hover:bg-brand-gold-light text-brand-maroon-dark font-bold text-sm sm:text-base px-4 sm:px-6 py-2.5 sm:py-3 rounded-full shadow-gold-glow transition-all duration-200 hover:scale-105"
            >
              <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform" />
              <span className={`whitespace-nowrap ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                {isHi ? 'अभी ऑर्डर करें' : 'Order Now'}
              </span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StickyCTA;
